import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight, faMoon, faSun } from "@fortawesome/pro-regular-svg-icons";
import { createPortal } from "react-dom";
import client from "../../../client";
import type { Language } from "../../../client/api";
import { showTranslated, translate } from "../../../config/translator";
import { isContentManager } from "../../../config/utils";
import { BETA_ENDPOINT, DEV_ENDPOINT, PROD_ENDPOINT, STAG_ENDPOINT } from "../../../config/constants";
import { ClavaContext, ClavaRootContext } from "../../../config/contexts";
import RoundedRectangle from "../RoundedRectangle";

const endpoints = {
  Production: PROD_ENDPOINT,
  Staging: STAG_ENDPOINT,
  Beta: BETA_ENDPOINT,
  Development: DEV_ENDPOINT
};

type SettingsPanelProps = {
  isOpen: boolean;
  onClose: () => void;
  buttonRef: React.RefObject<HTMLButtonElement>;
  changeLanguageAction: (language: Language) => void;
  setEndpoint: (endpoint: string) => void;
};

const SettingsPanel: React.FC<SettingsPanelProps> = ({
                                                       isOpen,
                                                       onClose,
                                                       buttonRef,
                                                       changeLanguageAction,
                                                       setEndpoint
                                                     }) => {
  const { l, user } = useContext(ClavaContext);
  const { theme, setTheme } = useContext(ClavaRootContext);
  const panelRef = useRef<HTMLDivElement>(null);

  const [languages, setLanguages] = useState<Language[]>([]);
  const [uiStep, setUiStep] = useState<1 | 2 | 3>(1);
  const [fadeIn, setFadeIn] = useState(true);
  const [slideDir, setSlideDir] = useState<1 | -1>(1);
  const [panelPosition, setPanelPosition] = useState<{ top: number; right: number } | null>(null);
  const [selectedEndpoint, setSelectedEndpoint] = useState<keyof typeof endpoints>(
    client().getEndpoint() === PROD_ENDPOINT
      ? "Production"
      : client().getEndpoint() === STAG_ENDPOINT
        ? "Staging"
        : client().getEndpoint() === BETA_ENDPOINT
          ? "Beta"
          : "Development"
  );

  useEffect(() => {
    let mounted = true;
    client().getLanguages().then((ls) => {
      if (mounted) setLanguages(ls);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const top = rect.bottom + 8;
      const right = window.innerWidth - rect.right;
      setPanelPosition({ top, right });
    }
  }, [buttonRef]);

  useEffect(() => {
    if (isOpen) {
      setUiStep(1);
      setFadeIn(true);
      setSlideDir(1);
      updatePosition();
    }
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    setFadeIn(false);
    const id = requestAnimationFrame(() => setFadeIn(true));
    return () => cancelAnimationFrame(id);
  }, [uiStep]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose();
        setUiStep(1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  const onSetLightTheme = useCallback(() => {
    if (theme !== "light") {
      setTheme?.("light");
    }
  }, [theme, setTheme]);

  const onSetDarkTheme = useCallback(() => {
    if (theme !== "dark") {
      setTheme?.("dark");
    }
  }, [theme, setTheme]);

  const onLanguageSelect = useCallback((lang: Language) => {
    changeLanguageAction(lang);
  }, [changeLanguageAction]);

  const onServerSelect = useCallback((endpointKey: keyof typeof endpoints) => {
    setSelectedEndpoint(endpointKey);
    setEndpoint(endpoints[endpointKey]);
  }, [setEndpoint]);

  const onOpenLanguage = useCallback(() => {
    setSlideDir(1);
    setFadeIn(false);
    setUiStep(2);
    requestAnimationFrame(() => setFadeIn(true));
  }, []);

  const onOpenServer = useCallback(() => {
    setSlideDir(1);
    setFadeIn(false);
    setUiStep(3);
    requestAnimationFrame(() => setFadeIn(true));
  }, []);

  const onBack = useCallback(() => {
    setSlideDir(-1);
    setFadeIn(false);
    setUiStep(1);
    requestAnimationFrame(() => setFadeIn(true));
  }, []);

  if (!isOpen || !panelPosition) return null;

  const tTheme = translate("theme", l);
  const tLanguage = translate("language", l);
  const tServer = translate("server", l);

  return createPortal(
    <div ref={panelRef}
         className="fixed z-[1000]"
         style={{ top: `${panelPosition.top}px`, right: `${panelPosition.right}px` }}>
      <RoundedRectangle
        className="bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark shadow-card dark:shadow-card-dark overflow-visible"
        hasBorder
      >
        <div className="w-60 p-3">
          <div
            key={`step-${uiStep}`}
            className={`flex flex-col min-h-0 transition-transform duration-300 ease-in-out will-change-transform ${
              fadeIn
                ? "opacity-100 translate-x-0"
                : slideDir === 1
                  ? "opacity-0 translate-x-6"
                  : "opacity-0 -translate-x-6"
            }`}
          >
            {/* Step 1: Main Menu */}
            {uiStep === 1 && (
              <div className="flex flex-col">
                {/* Theme Row */}
                <div className="flex items-center justify-between py-1 px-2">
                  <span className="text-xs font-medium text-font dark:text-font-dark">{tTheme}</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={onSetLightTheme}
                      className="p-1 transition-colors"
                      aria-label="Light mode"
                    >
                      <FontAwesomeIcon
                        icon={faSun}
                        className={`w-5 h-5 ${theme === "light" ? "text-primary" : "text-font/30 dark:text-font-dark/30"}`}
                      />
                    </button>
                    <div className="w-px h-5 bg-light-gray dark:bg-light-gray-dark" />
                    <button
                      type="button"
                      onClick={onSetDarkTheme}
                      className="p-1 transition-colors"
                      aria-label="Dark mode"
                    >
                      <FontAwesomeIcon
                        icon={faMoon}
                        className={`w-5 h-5 ${theme === "dark" ? "text-primary" : "text-font/30 dark:text-font-dark/30"}`}
                      />
                    </button>
                  </div>
                </div>

                {/* Language Row */}
                <button
                  type="button"
                  onClick={onOpenLanguage}
                  className="flex items-center justify-between py-1 rounded-md hover:bg-primary-50 dark:hover:bg-primary-50-dark transition-colors w-full px-2"
                >
                  <span className="text-xs font-medium text-font dark:text-font-dark">{tLanguage}</span>
                  <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4 text-font dark:text-font-dark" />
                </button>

                {/* Server Row (only for admin) */}
                {isContentManager(user) && (
                  <button
                    type="button"
                    onClick={onOpenServer}
                    className="flex items-center justify-between py-1 rounded-md hover:bg-primary-50 dark:hover:bg-primary-50-dark transition-colors w-full px-2"
                  >
                    <span className="text-xs font-medium text-font dark:text-font-dark">{tServer}</span>
                    <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4 text-font dark:text-font-dark" />
                  </button>
                )}
              </div>
            )}

            {/* Step 2: Language Selection */}
            {uiStep === 2 && (
              <div>
                <div className="flex flex-row items-center gap-2 mb-3 pb-2">
                  <button
                    type="button"
                    onClick={onBack}
                    aria-label="Back"
                  >
                    <FontAwesomeIcon
                      icon={faChevronLeft}
                      className="w-4 h-4 text-font dark:text-font-dark"
                    />
                  </button>
                  <h4 className="text-sm font-regular text-font-600 dark:text-font-600-dark truncate">
                    {tLanguage}
                  </h4>
                </div>
                <div className="flex flex-col">
                  {languages.map((lang) => (
                    <button
                      key={lang.id}
                      type="button"
                      onClick={() => onLanguageSelect(lang)}
                      className={`text-left px-3 py-1 rounded-md transition-colors text-xs font-medium ${
                        user?.language?.id === lang.id
                          ? "text-primary dark:text-primary"
                          : "hover:bg-primary-50 dark:hover:bg-primary-50-dark text-font dark:text-font-dark"
                      }`}
                    >
                      {showTranslated(lang.name, l)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Server Selection */}
            {uiStep === 3 && (
              <div>
                <div className="flex flex-row items-center gap-2 mb-3 pb-2">
                  <button
                    type="button"
                    onClick={onBack}
                    aria-label="Back"
                  >
                    <FontAwesomeIcon
                      icon={faChevronLeft}
                      className="w-4 h-4 text-font dark:text-font-dark"
                    />
                  </button>
                  <h4 className="text-sm font-regular text-font-600 dark:text-font-600-dark truncate">
                    {tServer}
                  </h4>
                </div>
                <div className="flex flex-col">
                  {Object.keys(endpoints).map((key) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => onServerSelect(key as keyof typeof endpoints)}
                      className={`text-left px-3 py-1 rounded-md transition-colors text-xs font-medium ${
                        selectedEndpoint === key
                          ? "text-primary dark:text-primary"
                          : "hover:bg-primary-50 dark:hover:bg-primary-50-dark text-font dark:text-font-dark"
                      }`}
                    >
                      {key}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </RoundedRectangle>
    </div>,
    document.body
  );
};

export default SettingsPanel;
